/**
 * Operadores de PERÍODO para condições de data em filtros e na busca avançada
 * (itens 52 e 9). Diferente dos operadores que comparam com uma data específica
 * (equals, greater_than...), estes resolvem um intervalo relativo no momento da
 * avaliação: mês atual, semana atual, hoje, últimos N dias, e um range livre.
 *
 * O front usa resolveDatePeriodRange() para filtrar em memória; o backend
 * (FilterService::applyCondition) reconhece os mesmos nomes de operador e
 * calcula o range com Carbon. Os nomes precisam bater nos dois lados.
 */

/**
 * Os períodos NEGADOS, e de qual período cada um é o avesso.
 *
 * ## Por que existem
 *
 * O operador "Diferente do dia" (`not_equals`) só aceitava uma data concreta:
 * dava para dizer "é deste mês", mas não "NÃO é deste mês". Pedido de
 * 14/10/2026 -- "quando selecionado Diferente do dia, deveria ter como
 * selecionar hoje, ontem, o mês, a semana e uma data personalizada".
 *
 * São operadores próprios, e não um valor especial dentro de `not_equals`,
 * porque assim reaproveitam o caminho que já existe dos dois lados: aqui, um
 * intervalo resolvido e invertido; no servidor, o mesmo intervalo com
 * `whereNotBetween`. Um valor-marcador novo seria uma convenção a mais para as
 * duas pontas combinarem -- e é exatamente onde esta base já se machucou.
 *
 * "Data personalizada" continua sendo o `not_equals` de sempre.
 */
export const NEGACAO_DO_PERIODO = {
    not_today: 'today',
    not_yesterday: 'yesterday',
    not_this_week: 'this_week',
    not_this_month: 'this_month',
};

export const DATE_PERIOD_OPERATORS = [
    'today',
    'yesterday',
    'specific_day',
    'this_week',
    'last_week',
    'specific_week',
    'this_month',
    'previous_month',
    'specific_month',
    'last_7_days',
    'last_30_days',
    'between',
    ...Object.keys(NEGACAO_DO_PERIODO),
];

export const DATE_PERIOD_LABELS = {
    today: 'Hoje',
    yesterday: 'Ontem',
    specific_day: 'Dia específico',
    this_week: 'Esta semana',
    last_week: 'Semana passada',
    specific_week: 'Semana específica',
    this_month: 'Este mês',
    previous_month: 'Mês anterior',
    specific_month: 'Mês específico',
    last_7_days: 'Últimos 7 dias',
    last_30_days: 'Últimos 30 dias',
    between: 'Entre (intervalo)',
    not_today: 'Diferente de hoje',
    not_yesterday: 'Diferente de ontem',
    not_this_week: 'Diferente desta semana',
    not_this_month: 'Diferente deste mês',
};

export const isDatePeriodOperator = (operator) => DATE_PERIOD_OPERATORS.includes(operator);

/** Operadores de período que NÃO pedem valor do usuário (são autocalculados). */
export const isValuelessDatePeriodOperator = (operator) =>
    isDatePeriodOperator(operator)
    && operator !== 'between'
    && operator !== 'specific_month'
    && operator !== 'specific_day'
    && operator !== 'specific_week';

const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
};

const endOfDay = (d) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x.getTime();
};

// Interpreta "YYYY-MM-DD" (input date) ou uma data qualquer (ou array [v]) como Date local.
const parseDateValue = (value) => {
    const raw = Array.isArray(value) ? value[0] : value;
    const str = String(raw || '').trim();
    if (!str) return null;
    const m = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    const d = m
        ? new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10))
        : new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Intervalo [startMs, endMs] de um operador de período, avaliado agora.
 * `value` só é usado por `between` (array [dataInicio, dataFim]).
 * Semana começa na segunda-feira. Retorna null se não for período ou se
 * `between` vier incompleto.
 */
export const resolveDatePeriodRange = (operator, value) => {
    const now = new Date();

    switch (operator) {
        case 'today':
            return { startMs: startOfDay(now), endMs: endOfDay(now) };

        case 'yesterday': {
            const y = new Date(now);
            y.setDate(now.getDate() - 1);
            return { startMs: startOfDay(y), endMs: endOfDay(y) };
        }

        case 'specific_day': {
            const d = parseDateValue(value);
            if (!d) return null;
            return { startMs: startOfDay(d), endMs: endOfDay(d) };
        }

        case 'this_week': {
            const day = now.getDay(); // 0=domingo ... 6=sábado
            const diffToMonday = (day + 6) % 7; // segunda = início
            const monday = new Date(now);
            monday.setDate(now.getDate() - diffToMonday);
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);
            return { startMs: startOfDay(monday), endMs: endOfDay(sunday) };
        }

        case 'last_week': {
            const day = now.getDay();
            const diffToMonday = (day + 6) % 7;
            const monday = new Date(now);
            monday.setDate(now.getDate() - diffToMonday - 7); // segunda da semana passada
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);
            return { startMs: startOfDay(monday), endMs: endOfDay(sunday) };
        }

        case 'specific_week': {
            const base = parseDateValue(value); // qualquer dia da semana desejada
            if (!base) return null;
            const day = base.getDay();
            const diffToMonday = (day + 6) % 7;
            const monday = new Date(base);
            monday.setDate(base.getDate() - diffToMonday);
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);
            return { startMs: startOfDay(monday), endMs: endOfDay(sunday) };
        }

        case 'this_month': {
            const first = new Date(now.getFullYear(), now.getMonth(), 1);
            const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            return { startMs: startOfDay(first), endMs: endOfDay(last) };
        }

        case 'previous_month': {
            // Mês-calendário inteiro anterior ao atual (não "30 dias pra trás").
            // Dia 0 do mês atual = último dia do mês anterior.
            const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const last = new Date(now.getFullYear(), now.getMonth(), 0);
            return { startMs: startOfDay(first), endMs: endOfDay(last) };
        }

        case 'specific_month': {
            // value = "YYYY-MM" (input type=month); aceita tambem Date/array.
            let raw = Array.isArray(value) ? value[0] : value;
            const str = String(raw || '').trim();
            let y;
            let m;
            const match = str.match(/^(\d{4})-(\d{1,2})/);
            if (match) {
                y = parseInt(match[1], 10);
                m = parseInt(match[2], 10) - 1;
            } else {
                const d = new Date(str);
                if (Number.isNaN(d.getTime())) return null;
                y = d.getFullYear();
                m = d.getMonth();
            }
            if (Number.isNaN(y) || Number.isNaN(m)) return null;
            const first = new Date(y, m, 1);
            const last = new Date(y, m + 1, 0);
            return { startMs: startOfDay(first), endMs: endOfDay(last) };
        }

        case 'last_7_days': {
            const from = new Date(now);
            from.setDate(now.getDate() - 6); // inclui hoje = 7 dias
            return { startMs: startOfDay(from), endMs: endOfDay(now) };
        }

        case 'last_30_days': {
            const from = new Date(now);
            from.setDate(now.getDate() - 29);
            return { startMs: startOfDay(from), endMs: endOfDay(now) };
        }

        case 'between': {
            const arr = Array.isArray(value) ? value : String(value || '').split(',');
            const a = arr[0] != null && arr[0] !== '' ? new Date(arr[0]) : null;
            const b = arr[1] != null && arr[1] !== '' ? new Date(arr[1]) : null;
            if (!a || !b || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
            // aceita as datas em qualquer ordem
            const lo = Math.min(a.getTime(), b.getTime());
            const hi = Math.max(a.getTime(), b.getTime());
            return { startMs: startOfDay(lo), endMs: endOfDay(hi) };
        }

        default:
            return null;
    }
};

/**
 * Um ticket cai no período? Usado pelo front para filtrar em memória.
 *
 * Nos períodos NEGADOS a resposta é o avesso: resolve o intervalo do período
 * base e devolve "está fora dele". Chamado sem data continua FORA dos dois --
 * é o mesmo que `not_equals` já fazia aqui, e o mesmo que o `whereNotBetween`
 * faz no servidor, onde `NULL` nunca casa. Os dois lados concordam, que é o
 * que importa: um chamado aparecer na tela e sumir ao recarregar seria pior
 * que qualquer uma das duas escolhas.
 */
export const ticketMatchesDatePeriod = (ticketValueRaw, operator, value) => {
    const negado = NEGACAO_DO_PERIODO[operator];
    const range = resolveDatePeriodRange(negado || operator, value);
    if (!range) return false;
    if (ticketValueRaw == null || ticketValueRaw === '') return false;
    const ms = new Date(ticketValueRaw).getTime();
    if (Number.isNaN(ms)) return false;
    const dentro = ms >= range.startMs && ms <= range.endMs;
    return negado ? !dentro : dentro;
};
